import { Calendar, User, Tag, ArrowLeft, Clock, Share2, Phone, Mail, MapPin, Building, Users, Target, ExternalLink, Truck } from 'lucide-react'
import { Link } from 'react-router-dom'

export default function ScotlandExpansion() {
  const shareUrl = window.location.href
  const shareTitle = "C9 Recruitment Expands to Scotland"

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        })
      } catch (error) {
        console.log('Error sharing:', error)
      }
    }
  }

  const scottishLocations = [
    {
      city: "Glasgow",
      opportunities: "Industrial estates, port logistics, retail distribution",
      sectors: "Manufacturing, retail, import/export",
      highlights: "Major logistics hub with excellent transport links"
    },
    {
      city: "Edinburgh",
      opportunities: "Urban delivery, construction logistics, specialist transport",
      sectors: "Construction, retail, pharmaceutical",
      highlights: "Growing e-commerce and urban logistics market"
    },
    {
      city: "Aberdeen",
      opportunities: "Oil & gas logistics, port operations, offshore support",
      sectors: "Energy, maritime, heavy industry",
      highlights: "Specialist high-value cargo and energy sector transport"
    },
    {
      city: "Dundee",
      opportunities: "Regional distribution, food logistics, manufacturing",
      sectors: "Food production, manufacturing, retail",
      highlights: "Strategic location for central Scotland distribution"
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-c9-blue to-c9-blue-alt text-white py-16">
        <div className="container-full">
          <div className="flex items-center gap-4 mb-8">
            <Link 
              to="/blog" 
              className="flex items-center gap-2 text-c9-orange hover:text-white transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Blog
            </Link>
          </div>
          
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-4 mb-6 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>3rd June 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>6 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Sam Wharfe (CEO, C9 Recruitment)</span>
              </div>
              <span className="bg-c9-orange px-3 py-1 rounded-full text-xs font-medium">Company News</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight">
              C9 Recruitment <span className="text-c9-orange">Expands to Scotland</span>
            </h1>
            
            <p className="text-xl mb-8 leading-relaxed opacity-90">
              We're excited to announce our expansion into Scotland, bringing our specialist transport recruitment services to drivers and employers across the country. Discover the opportunities this creates for Scottish logistics professionals.
            </p>
            
            <div className="flex items-center gap-4">
              <button 
                onClick={handleShare}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors"
              >
                <Share2 className="h-4 w-4" />
                Share Article
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16">
        <div className="container-full">
          <div className="article-content">
            <div className="article-prose">
              
              <h2 className="flex items-center gap-3">
                <MapPin className="h-6 w-6 text-c9-orange" />
                Why Scotland, Why Now?
              </h2>
              
              <p>Scotland's transport and logistics sector is experiencing unprecedented growth, driven by increasing demand for goods movement, infrastructure investment, and the country's strategic position as a gateway to the Highlands, Islands, and Europe.</p>
              
              <p>Our expansion into Scotland represents a natural progression for C9 Recruitment. We've identified significant opportunities to support Scottish drivers and employers with our specialist recruitment expertise and nationwide network.</p>

              <div className="article-highlight">
                <div className="flex items-start gap-3">
                  <Building className="h-6 w-6 text-c9-orange mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-c9-blue mb-2">Strategic Expansion</h3>
                    <p className="text-gray-700">Scotland represents a £2.5 billion logistics market with growing demand for skilled HGV drivers across all sectors, from urban delivery to specialist transport.</p>
                  </div>
                </div>
              </div>

              <h2>Scotland's Logistics Landscape</h2>
              
              <p>The Scottish transport sector is diverse and dynamic, offering opportunities across multiple industries:</p>
              
              <h3>Key Industries</h3>
              <ul>
                <li><strong>Food & Drink:</strong> Scotland's famous exports require reliable transport solutions</li>
                <li><strong>Energy Sector:</strong> Oil, gas, and renewable energy infrastructure</li>
                <li><strong>Manufacturing:</strong> From traditional industries to high-tech production</li>
                <li><strong>Retail & E-commerce:</strong> Growing online delivery requirements</li>
                <li><strong>Construction:</strong> Major infrastructure projects across Scotland</li>
                <li><strong>Tourism:</strong> Seasonal transport needs for Scotland's tourism industry</li>
              </ul>

              <h3>Geographic Opportunities</h3>
              <p>Scotland's unique geography creates varied transport challenges and opportunities:</p>
              
              <ul>
                <li><strong>Central Belt:</strong> High-density urban logistics between Glasgow and Edinburgh</li>
                <li><strong>Highlands & Islands:</strong> Specialist routes requiring experienced drivers</li>
                <li><strong>Border Regions:</strong> Cross-border trade with England and international markets</li>
                <li><strong>Coastal Areas:</strong> Port logistics and ferry connections</li>
              </ul>

              <h2>Regional Focus Areas</h2>
              
              <div className="grid gap-6 mt-8">
                {scottishLocations.map((location, index) => (
                  <div key={index} className="article-card">
                    <div className="flex items-start gap-4">
                      <div className="bg-c9-orange/10 p-3 rounded-lg">
                        <MapPin className="h-6 w-6 text-c9-orange" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-c9-blue mb-2">{location.city}</h3>
                        <p className="text-gray-600 mb-3">{location.highlights}</p>
                        <div className="space-y-2">
                          <div>
                            <span className="font-medium text-gray-700">Key Sectors: </span>
                            <span className="text-gray-600">{location.sectors}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Opportunities: </span>
                            <span className="text-gray-600">{location.opportunities}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <h2>What We Bring to Scotland</h2>
              
              <h3>For Scottish Drivers</h3>
              <ul>
                <li><strong>Access to nationwide opportunities:</strong> Work across the UK with Scottish-based roles</li>
                <li><strong>Specialist sector expertise:</strong> Connections with premium employers</li>
                <li><strong>Career development support:</strong> Training opportunities and career progression</li>
                <li><strong>Competitive packages:</strong> Above-market rates and excellent benefits</li>
                <li><strong>Personal service:</strong> Dedicated consultants who understand your needs</li>
              </ul>

              <h3>For Scottish Employers</h3>
              <ul>
                <li><strong>Access to qualified drivers:</strong> Nationwide candidate pool</li>
                <li><strong>Specialist screening:</strong> Comprehensive vetting and skills assessment</li>
                <li><strong>Market intelligence:</strong> Salary benchmarking and industry insights</li>
                <li><strong>Reduced recruitment costs:</strong> Efficient placement process</li>
                <li><strong>Ongoing support:</strong> Long-term partnership approach</li>
              </ul>

              <div className="article-success">
                <div className="flex items-start gap-3">
                  <Users className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-green-800 mb-2">Partnership Approach</h3>
                    <p className="text-green-700">We're not just expanding into Scotland – we're investing in long-term relationships with the Scottish logistics community, supporting growth and success for all our partners.</p>
                  </div>
                </div>
              </div>

              <h2>Job Opportunities Across Scotland</h2>
              
              <h3>Driver Categories in High Demand</h3>
              <ul>
                <li><strong>Class 1 (C+E) Drivers:</strong> Long-haul and regional distribution</li>
                <li><strong>Class 2 (C) Drivers:</strong> Local delivery and urban logistics</li>
                <li><strong>ADR Qualified:</strong> Hazardous goods transport (high demand)</li>
                <li><strong>HIAB Operators:</strong> Construction and specialist lifting</li>
                <li><strong>Tanker Drivers:</strong> Fuel, chemicals, and liquid transport</li>
                <li><strong>Multi-drop Drivers:</strong> Urban delivery positions in major cities</li>
                <li><strong>Tramper Drivers:</strong> UK-wide roles based from Scottish depots</li>
              </ul>

              <h3>Salary Expectations</h3>
              <p>Scottish logistics salaries are competitive and often offer excellent value for money:</p>
              
              <div className="article-card">
                <h4 className="font-bold text-c9-blue mb-3">Scottish Salary Ranges (2025)</h4>
                <ul className="space-y-2">
                  <li><strong>Class 2 Drivers:</strong> £28,000 - £32,000</li>
                  <li><strong>Class 1 Drivers:</strong> £32,000 - £40,000</li>
                  <li><strong>Specialist Drivers:</strong> £35,000 - £45,000</li>
                  <li><strong>Owner Operators:</strong> £45,000 - £60,000+</li>
                </ul>
              </div>

              <h2>Getting Started</h2>
              
              <h3>For Drivers</h3>
              <p>If you're a Scottish driver looking for new opportunities:</p>
              
              <ol>
                <li><strong>Register with us:</strong> Complete our online registration or call our Scottish team</li>
                <li><strong>Skills assessment:</strong> We'll discuss your experience, qualifications, and preferences</li>
                <li><strong>Opportunity matching:</strong> We'll match you with suitable roles across Scotland</li>
                <li><strong>Interview support:</strong> We'll prepare you for interviews and provide feedback</li>
                <li><strong>Ongoing support:</strong> Continuous career development and new opportunity alerts</li>
              </ol>

              <h3>For Employers</h3>
              <p>Scottish employers looking for reliable drivers:</p>
              
              <ol>
                <li><strong>Consultation:</strong> Discuss your specific requirements and challenges</li>
                <li><strong>Sourcing strategy:</strong> Develop a targeted approach to find suitable candidates</li>
                <li><strong>Candidate screening:</strong> Comprehensive vetting and skills assessment</li>
                <li><strong>Interview coordination:</strong> Manage the entire recruitment process</li>
                <li><strong>Ongoing partnership:</strong> Long-term recruitment support and account management</li>
              </ol>

              <div className="article-warning">
                <div className="flex items-start gap-3">
                  <Target className="h-6 w-6 text-yellow-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-yellow-800 mb-2">Early Opportunities</h3>
                    <p className="text-yellow-700">As we establish our Scottish presence, we're actively seeking drivers and employers to join our network. Early partners will benefit from dedicated support and priority access to opportunities.</p>
                  </div>
                </div>
              </div>

              <h2>Looking Ahead</h2>
              
              <p>Our Scottish expansion is just the beginning. We're committed to building long-term relationships with Scottish drivers and employers, supporting the growth of the logistics sector, and contributing to Scotland's economic success.</p>
              
              <p>As we establish ourselves in Scotland, we'll continue to invest in local expertise, technology, and partnerships to provide the best possible recruitment service. We're excited about the opportunities ahead and look forward to working with the Scottish logistics community.</p>

              <p>Scotland's strategic importance to UK logistics, combined with its unique transport challenges and opportunities, makes it an essential market for C9 Recruitment. We're here to support the continued growth and success of Scottish transport and logistics.</p>

            </div>
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="bg-c9-blue text-white section-padding">
        <div className="container-full text-center">
          <div className="max-w-3xl mx-auto">
            <div className="article-cta">
              <h3 className="text-2xl font-bold mb-4 flex items-center justify-center gap-2">
                <Truck className="h-6 w-6" />
                Ready to Explore Scottish Opportunities?
              </h3>
              <p className="text-lg mb-6">
                Whether you're a driver looking for new opportunities or an employer seeking reliable staff, we're here to help you succeed in Scotland's growing logistics sector.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/candidates" 
                  className="flex items-center justify-center gap-2 bg-white text-c9-orange hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors"
                >
                  <ExternalLink className="h-5 w-5" />
                  Browse Scottish Opportunities
                </Link>
                <a 
                  href="tel:02081524574" 
                  className="flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-c9-blue px-8 py-3 rounded-lg font-medium transition-colors"
                >
                  <Phone className="h-5 w-5" />
                  Discuss Scottish Expansion
                </a>
              </div>
              
              <div className="mt-8 text-sm text-gray-200">
                <p>Connect with our Scottish team for personalised support</p>
                <div className="flex items-center justify-center gap-6 mt-3">
                  <a href="tel:02081524574" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Phone className="h-4 w-4" />
                    0208 152 4574
                  </a>
                  <a href="mailto:commercial@c9-recruitment.com" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Mail className="h-4 w-4" />
                    commercial@c9-recruitment.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
